import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import ContactCard from '../contacts/ContactCard';
import ContactsModal, { Contact } from '../contacts/ContactsModal';
import ConfirmDeleteModal from '../../organisms/ConfirmDeleteModal';
import api, { getUserUid } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';

interface ContactsTabProps {
  shouldOpenNewContact?: boolean;
  onNewContactOpened?: () => void;
  boardId?: string;
}

interface ContactFromAPI {
  uid: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  companies_name?: string[];
  email?: string[];
  phone?: string[];
  linkedin_url?: string;
  twitter_url?: string;
  meta_url?: string;
  github_url?: string;
  board_linked?: string;
  job_linked?: string;
}

interface ContactModalData {
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  location?: string;
  email?: string;
  phone?: string;
  twitter?: string;
  facebook?: string;
  linkedin?: string;
  github?: string;
  boardLinked?: string;
  jobLinked?: string;
}

const ContactsTab: React.FC<ContactsTabProps> = ({ shouldOpenNewContact, onNewContactOpened, boardId }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactModalData | null>(null);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    contactId: string | null;
    contactName: string;
  }>({
    isOpen: false,
    contactId: null,
    contactName: '',
  });

  const transformContact = (apiContact: ContactFromAPI): Contact => {
    if (!apiContact.uid) {

      throw new Error('Contact must have uid');
    }
    return {
      id: apiContact.uid,
      firstName: apiContact.first_name,
      lastName: apiContact.last_name,
      title: apiContact.job_title || '',
      company: apiContact.companies_name?.[0] || '',
      email: apiContact.email?.[0] || '',
      phone: apiContact.phone?.[0] || '',
      twitter: apiContact.twitter_url || undefined,
      linkedin: apiContact.linkedin_url || undefined,
      facebook: apiContact.meta_url || undefined,
      github: apiContact.github_url || undefined,
      boardLinked: apiContact.board_linked,
      jobLinked: apiContact.job_linked,
    };
  };

  useEffect(() => {
    const fetchContacts = async () => {
      if (!boardId) {

        setContacts([]);
        setLoadingContacts(false);
        return;
      }

      try {
        setLoadingContacts(true);
        const data = await api.get<ContactFromAPI[]>(API_ENDPOINTS.contacts.getByKanban(boardId));
        
        if (Array.isArray(data)) {
          const transformedContacts = data.map(transformContact);
          setContacts(transformedContacts);
        } else {

          setContacts([]);
        }
      } catch (error) {

        setContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    fetchContacts();
  }, [boardId]);

  useEffect(() => {
    if (shouldOpenNewContact) {
      setEditingContact(null);
      setEditingContactId(null);
      setModalOpen(true);
      onNewContactOpened?.();
    }
  }, [shouldOpenNewContact, onNewContactOpened]);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const refreshContacts = async () => {
    if (!boardId) return;

    try {
      const data = await api.get<ContactFromAPI[]>(API_ENDPOINTS.contacts.getByKanban(boardId));
      if (Array.isArray(data)) {
        const transformedContacts = data.map(transformContact);
        setContacts(transformedContacts);
      }
    } catch (error) {

    }
  };

  const handleNewContact = () => {
    setEditingContact(null);
    setEditingContactId(null);
    setModalOpen(true);
  };

  const handleEditContact = (id?: string) => {
    if (!id) return;
    const contact = contacts.find(c => c.id === id);
    if (!contact) return;

    const modalData: ContactModalData = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      title: contact.title,
      company: contact.company,
      email: contact.email || undefined,
      phone: contact.phone || undefined,
      twitter: contact.twitter || undefined,
      linkedin: contact.linkedin || undefined,
      facebook: contact.facebook || undefined,
      github: contact.github || undefined,
      boardLinked: contact.boardLinked,
      jobLinked: contact.jobLinked,
    };

    setEditingContact(modalData);
    setEditingContactId(id);
    setModalOpen(true);
  };

  const handleDeleteContact = (id: string) => {
    if (!id) {

      return;
    }
    const contact = contacts.find(c => c.id === id);
    if (!contact) {

      return;
    }
    const contactName = contact.firstName && contact.lastName 
      ? `${contact.firstName} ${contact.lastName}`.trim()
      : contact.firstName || contact.lastName || 'Unknown';
    
    setDeleteModal({
      isOpen: true,
      contactId: id,
      contactName,
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.contactId === null) return;

    try {
      await api.delete(API_ENDPOINTS.contacts.delete(deleteModal.contactId));

      setDeleteModal({
        isOpen: false,
        contactId: null,
        contactName: '',
      });

      await refreshContacts();
    } catch (error: any) {


      if (error?.status === 404) {
        setDeleteModal({
          isOpen: false,
          contactId: null,
          contactName: '',
        });
        await refreshContacts();
      } else if (error?.status === 401) {

        setDeleteModal({
          isOpen: false,
          contactId: null,
          contactName: '',
        });
      } else {

        setDeleteModal({
          isOpen: false,
          contactId: null,
          contactName: '',
        });
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({
      isOpen: false,
      contactId: null,
      contactName: '',
    });
  };

  const handleEditSubmit = async (data: ContactModalData) => {
    if (editingContactId === null) return;
    
    try {
      if (!data.firstName?.trim()) {

        return;
      }
      
      if (!data.lastName?.trim()) {

        return;
      }
      
      if (data.email && !isValidEmail(data.email)) {

        return;
      }
      
      const contactPayload = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        job_title: data.title?.trim() || null,
        companies_name: data.company?.trim() ? [data.company.trim()] : null,
        email: data.email?.trim() ? [data.email.trim()] : null,
        phone: data.phone?.trim() ? [data.phone.trim()] : null,
        linkedin_url: data.linkedin?.trim() || null,
        twitter_url: data.twitter?.trim() || null,
        meta_url: data.facebook?.trim() || null,
        github_url: data.github?.trim() || null,
        board_linked: data.boardLinked || null,
        job_linked: data.jobLinked || null,
      };

      await api.put(API_ENDPOINTS.contacts.update(editingContactId), contactPayload);
      
      setModalOpen(false);
      setEditingContact(null);
      setEditingContactId(null);
      
      await refreshContacts();
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else {

      }
    }
  };

  const handleCreateSubmit = async (data: ContactModalData) => {
    try {
      const user_uid = getUserUid();
      
      if (!user_uid) {

        return;
      }

      if (!boardId) {

        return;
      }

      if (!data.firstName?.trim()) {

        return;
      }
      
      if (!data.lastName?.trim()) {

        return;
      }
      
      if (data.email && !isValidEmail(data.email)) {

        return;
      }
      
      const contactPayload = {
        user_uid,
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        job_title: data.title?.trim() || null,
        companies_name: data.company?.trim() ? [data.company.trim()] : null,
        email: data.email?.trim() ? [data.email.trim()] : null,
        phone: data.phone?.trim() ? [data.phone.trim()] : null,
        linkedin_url: data.linkedin?.trim() || null,
        twitter_url: data.twitter?.trim() || null,
        meta_url: data.facebook?.trim() || null,
        github_url: data.github?.trim() || null,
        board_linked: boardId,
        job_linked: data.jobLinked || null,
      };

      await api.post(API_ENDPOINTS.contacts.create, contactPayload);
      
      setModalOpen(false);
      setEditingContact(null);
      
      await refreshContacts();
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else {

      }
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {loadingContacts ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
          Loading contacts...
        </div>
      ) : (
        <>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, 280px)', 
            gridAutoRows: '1fr',
            gap: '20px',
            justifyContent: 'start',
            alignItems: 'stretch'
          }}>
            {contacts.map(contact => {
              const contactId = contact.id;
              if (!contactId) {
                return null;
              }
              return (
                <ContactCard
                  key={contactId}
                  id={contactId}
                  firstName={contact.firstName}
                  lastName={contact.lastName}
                  title={contact.title || ''}
                  company={contact.company || ''}
                  email={contact.email || ''}
                  phone={contact.phone}
                  location={contact.location}
                  twitter={contact.twitter}
                  linkedin={contact.linkedin}
                  facebook={contact.facebook}
                  github={contact.github}
                  onEdit={() => handleEditContact(contactId)}
                  onDelete={() => handleDeleteContact(contactId)}
                />
              );
            })}
          </div>

          {contacts.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px', 
              color: '#6b7280', 
              fontSize: '14px' 
            }}>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                <Users size={48} style={{ color: '#9ca3af' }} />
              </div>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>No contacts yet</div>
              <div>Click "+ New contact" to add your first contact</div>
            </div>
          )}
        </>
      )}

      <ContactsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingContact(null);
          setEditingContactId(null);
        }}
        initial={editingContact || (boardId ? { boardLinked: boardId } : undefined)}
        onSubmit={editingContactId ? handleEditSubmit : handleCreateSubmit}
      />

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        itemName={deleteModal.contactName || 'this contact'}
        itemType="Contact"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ContactsTab;

